import { combineReducers } from 'redux';
import * as storage from 'redux-storage';
import createEngine from 'redux-storage-engine-sessionstorage';
import thunkMiddleware from 'redux-thunk';
import { connectRouter, routerMiddleware } from 'connected-react-router';
import { appReducers } from '../rootReducer';
import { CLEAR_STATE } from '../AppStateActions';
import SESSION_STORAGE_KEY from '../constants/appConstants';
import filter from 'redux-storage-decorator-filter';
import * as devConfig from './configureStore.dev';
import * as prodConfig from './configureStore.prod';
import { reducer as jPlayers } from 'react-jplayer';
import { reducer as jPlaylists } from 'react-jplaylist';
import lrsMiddleware from '../common/lrs/lrsMiddleware';
import troEventsMiddleware from '../trc/troMiddleware';
import requestPersistenceMiddleware from '../common/persistence/requestPersistenceMiddleware';
import { dataDogAddWarning } from '../common/logging/datadog';

const createStorageEngine = () => {
    const whiteList = [
        ['result', 'byId'],
        ['result', 'isDataFetched'],
        ['trc', 'bookResults'],
        ['lc', 'selectedPassageId'],
        ['lc', 'passageHalf'],
        'forms',
        'resume',
        'playlist',
        ['screen', 'isExternalURLSourcePresent'],
        ['screen', 'initialQueryParams'],
        ['screen', 'isBenchmarkModeEnabled'],
        ['screen', 'captions'],
        ['user', 'persistedUserId'],
    ];
    const blackList = [];

    let engine = createEngine(SESSION_STORAGE_KEY);
    engine = filter(engine, whiteList, blackList);

    return engine;
};

export default function configureStore(initialState, history) {
    let middlewares = [];

    const engine = createStorageEngine();

    middlewares = middlewares.concat([
        // Add other middleware on this line...

        // thunk middleware can also accept an extra argument to be passed to each thunk action
        // https://github.com/gaearon/redux-thunk#injecting-a-custom-argument
        thunkMiddleware,

        // persist redux state to sessionStorage
        storage.createMiddleware(engine),

        // redux routing
        routerMiddleware(history),

        requestPersistenceMiddleware,

        lrsMiddleware,

        troEventsMiddleware,
    ]);

    if (process.env.NODE_ENV !== 'production') {
        middlewares = middlewares.concat(devConfig.getDevMiddlewares());
    } else {
        middlewares = middlewares.concat(prodConfig.getProdMiddlewares());
    }

    const rootReducer = storage.reducer((state, action) => {
        const reducer = combineReducers({
            ...appReducers,
            jPlayers,
            jPlaylists,
            router: connectRouter(history),
        });

        if (action.type === CLEAR_STATE) {
            // jPlayer/jPlaylist reducers strongly rely on initial state, so don't reset it
            const { jPlayers, jPlaylists, ...reducersToReset } = state;
            state = {
                jPlayers,
                jPlaylists,
            };
        }

        return reducer(state, action);
    });

    let store;
    if (process.env.NODE_ENV === 'production') {
        store = prodConfig.createStore(rootReducer, initialState, middlewares);
    } else {
        store = devConfig.createStore(rootReducer, initialState, middlewares);
    }

    // load state from storage
    return storage.createLoader(engine)(store)
        .then(() => {
            return store;
        })
        .catch(() => {
            dataDogAddWarning('Failed to load previous state');
            console.error('Failed to load previous state');
        });
}
